/**
 * LoopDesk: help queries move to the central LoopDesk hub when the backend has a
 * LoopDesk key (backend/config/settings/loopdesk.py). The backend then mounts a proxy
 * at /api/v1/loopdesk/; without a key that path is a 404 and DrTutor keeps its own
 * help queries exactly as before.
 *
 *   useLoopDesk()          "loading" | "on" | "legacy", plus whether this user is an app admin
 *   useLoopDeskQueries()   the Changelog page's list, in the SupportQuery shape it already draws
 *   useLoopDeskCounts()    the header counts
 *   loopdeskFetch          the widget's fetch: DrTutor's Bearer token with the shared refresh
 */

import { useQuery, keepPreviousData, type UseQueryResult } from "@tanstack/react-query";

import { basePath } from "@/lib/tenant/basePath";

import { authedFetch } from "./client";
import type { QueryCounts, QueryStatus, SupportQuery, SupportQueryList } from "./support";
import { toQueryString, type Paginated } from "./types";

export const LOOPDESK_BASE = `${basePath}/api/v1/loopdesk`;

export interface LoopDeskMe {
  enabled: boolean;
  is_app_admin: boolean;
  role: string;
  name: string;
  user_id: string;
  app: { name: string; prefix: string; audiences: string[] } | null;
}

export type LoopDeskState = "loading" | "on" | "legacy";

let mode: LoopDeskState = "loading";

/** True once LoopDesk answered for this user; openSupport() routes to it then. */
export function loopDeskOn(): boolean {
  return mode === "on";
}

export const loopdeskFetch = (input: string, init: RequestInit) => authedFetch(input, init);

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(`${LOOPDESK_BASE}/${path}`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw Object.assign(new Error(`LoopDesk ${res.status}`), { status: res.status });
  return (await res.json()) as T;
}

export function useLoopDesk(enabled = true): { state: LoopDeskState; me: LoopDeskMe | null } {
  const query = useQuery({
    queryKey: ["loopdesk", "me"],
    queryFn: () => getJson<LoopDeskMe>("me/"),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
  // Only "not connected" (404) or "not for your role" means the built-in queries. Any other
  // error (LoopDesk briefly down) keeps LoopDesk, whose panel shows the problem itself.
  const notConnected = query.isError && (query.error as { status?: number }).status === 404;
  let state: LoopDeskState = "loading";
  if (notConnected || (query.isSuccess && !query.data.enabled)) state = "legacy";
  else if (query.isSuccess || query.isError) state = "on";
  if (state !== "loading") mode = state;
  return { state, me: query.data ?? null };
}

interface LoopDeskRow {
  id: string;
  number: string;
  subject: string;
  status: QueryStatus | "declined";
  status_label: string;
  reporter: { external_id: string; name: string };
  message_count: number;
  last_message: string | null;
  has_attachments: boolean;
  last_activity_at: string;
  created_at: string;
  resolved_at: string | null;
}

function toSupportQuery(row: LoopDeskRow): SupportQuery {
  return {
    id: 0,
    number: row.number,
    subject: row.subject,
    // Declined has no DrTutor colour of its own; it reads as closed with its own label.
    status: (row.status === "declined" ? "closed" : row.status) as QueryStatus,
    status_label: row.status_label,
    reporter: { id: Number(row.reporter.external_id) || 0, name: row.reporter.name } as SupportQuery["reporter"],
    page_url: "",
    resolved_by: null,
    resolved_at: row.resolved_at,
    last_activity_at: row.last_activity_at,
    created_at: row.created_at,
    message_count: row.message_count,
    last_message: row.last_message ?? "",
    has_screenshot: row.has_attachments,
  };
}

export function useLoopDeskQueries(q: SupportQueryList, enabled: boolean): UseQueryResult<Paginated<SupportQuery>> {
  return useQuery({
    queryKey: ["loopdesk", "list", q],
    queryFn: async () => {
      const data = await getJson<Paginated<LoopDeskRow>>(`requests/${toQueryString(q)}`);
      return { ...data, results: data.results.map(toSupportQuery) };
    },
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useLoopDeskCounts(enabled: boolean): UseQueryResult<QueryCounts> {
  return useQuery({
    queryKey: ["loopdesk", "counts"],
    queryFn: () => getJson<QueryCounts>("requests/counts/"),
    enabled,
    refetchInterval: 30_000,
  });
}
